import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type AuditEntry, type BroadcastDetail, type BroadcastStat, type Subscriber } from '../api';
import { formatTimestamp } from '../lib/format';
import { renderEmail } from '../../functions/_lib/template';
import { PreviewModal } from '../components/PreviewModal';

// Audit rows (Sheets) and broadcast rows (D1) are written by the same send but
// carry no shared id, so we pair them by subject + closest timestamp.
const MATCH_TOLERANCE_MS = 45 * 60 * 1000;

function matchBroadcast(entry: AuditEntry, stats: BroadcastStat[]): BroadcastStat | null {
  if (entry.action !== 'broadcast') return null;
  const ts = new Date(entry.timestamp).getTime();
  if (Number.isNaN(ts)) return null;
  let best: BroadcastStat | null = null;
  let bestDelta = Infinity;
  for (const b of stats) {
    if (b.subject !== entry.subject) continue;
    const delta = Math.abs(b.createdAt - ts);
    if (delta < bestDelta) { best = b; bestDelta = delta; }
  }
  return bestDelta <= MATCH_TOLERANCE_MS ? best : null;
}

export function HistoryPage() {
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState<'all' | 'broadcast' | 'test'>('all');
  const [expanded, setExpanded] = useState<number | null>(null);
  const [preview, setPreview] = useState<{ html: string; subject: string } | null>(null);
  const [broadcastStats, setBroadcastStats] = useState<BroadcastStat[]>([]);
  const [subsByEmail, setSubsByEmail] = useState<Map<string, Subscriber>>(new Map());

  function openPreview(entry: AuditEntry) {
    if (!entry.bodyHtml) {
      alert('No body stored for this entry — only entries sent after the latest deploy include the email content.');
      return;
    }
    const html = renderEmail({
      subject: entry.subject || '(no subject)',
      bodyHtml: entry.bodyHtml,
      unsubscribeUrl: 'https://newsletter.atomic-rose.com/api/unsubscribe?e=preview&s=preview',
      recipientName: 'Friend',
      preheader: undefined,
    });
    setPreview({ html, subject: entry.subject || '(no subject)' });
  }

  useEffect(() => {
    refresh();
    // Subscriber tags power the "purchased?" indicator on clickers — best-effort.
    api.listSubscribers()
      .then((r) => {
        const m = new Map<string, Subscriber>();
        for (const s of r.subscribers) m.set(s.email.trim().toLowerCase(), s);
        setSubsByEmail(m);
      })
      .catch(() => setSubsByEmail(new Map()));
  }, []);

  function refresh() {
    setEntries(null);
    setError(null);
    api.listAudit(200)
      .then((r) => setEntries(r.entries))
      .catch((e) => setError(e.message));
    // Engagement stats are best-effort — empty when D1 isn't wired.
    api.listBroadcastStats()
      .then((r) => setBroadcastStats(r.broadcasts))
      .catch(() => setBroadcastStats([]));
  }

  const matchedStats = useMemo(() => {
    const m = new Map<AuditEntry, BroadcastStat>();
    for (const e of entries ?? []) {
      const b = matchBroadcast(e, broadcastStats);
      if (b) m.set(e, b);
    }
    return m;
  }, [entries, broadcastStats]);

  const filtered = useMemo(() => {
    if (!entries) return [];
    const q = search.trim().toLowerCase();
    return entries.filter((e) => {
      if (actionFilter !== 'all' && e.action !== actionFilter) return false;
      if (!q) return true;
      return (
        e.subject.toLowerCase().includes(q) ||
        e.admin.toLowerCase().includes(q) ||
        e.sources.toLowerCase().includes(q) ||
        e.testEmail.toLowerCase().includes(q)
      );
    });
  }, [entries, search, actionFilter]);

  const stats = useMemo(() => {
    if (!entries) return { broadcasts: 0, tests: 0, totalSent: 0 };
    return {
      broadcasts: entries.filter((e) => e.action === 'broadcast').length,
      tests: entries.filter((e) => e.action === 'test').length,
      totalSent: entries.reduce((acc, e) => acc + e.sent, 0),
    };
  }, [entries]);

  if (error) return <div className="error">Failed to load: {error}</div>;
  if (!entries) return <div className="muted">Loading history…</div>;

  return (
    <>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="row" style={{ gap: 32, justifyContent: 'space-between' }}>
          <div className="row" style={{ gap: 32 }}>
            <Stat label="Broadcasts" value={stats.broadcasts} />
            <Stat label="Test sends" value={stats.tests} />
            <Stat label="Total emails delivered" value={stats.totalSent} />
          </div>
          <button className="secondary" onClick={refresh} style={{ padding: '8px 16px', fontSize: 13 }}>
            Refresh
          </button>
        </div>
      </div>

      <div className="card">
        <div className="row" style={{ marginBottom: 16, gap: 12 }}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search subject, admin, tag, test email…"
            style={{ flex: 1 }}
          />
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value as typeof actionFilter)}
            style={{
              background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)',
              borderRadius: 8, padding: '10px 12px', font: 'inherit',
            }}
          >
            <option value="all">All actions</option>
            <option value="broadcast">Broadcasts only</option>
            <option value="test">Tests only</option>
          </select>
        </div>

        {filtered.length === 0 ? (
          <p className="muted" style={{ textAlign: 'center', padding: 24 }}>
            {entries.length === 0 ? 'No sends yet — fire one off and it will appear here.' : 'No entries match.'}
          </p>
        ) : (
          <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>Admin</th>
                <th>Action</th>
                <th>Subject</th>
                <th style={{ textAlign: 'right' }}>Sent / Failed</th>
                <th style={{ textAlign: 'right' }}>Opens / Clicks</th>
                <th style={{ width: 32 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e, i) => (
                <Fragment key={i}>
                  <tr
                    onClick={() => setExpanded(expanded === i ? null : i)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="muted" style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
                      {formatTimestamp(e.timestamp)}
                    </td>
                    <td style={{ fontSize: 13 }}>{e.admin || <span className="muted">—</span>}</td>
                    <td>
                      <span className={`badge ${e.action === 'broadcast' ? 'subscribed' : ''}`}>
                        {e.action}
                      </span>
                    </td>
                    <td>{e.subject || <span className="muted">—</span>}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      <strong>{e.sent}</strong>
                      <span className="muted"> / </span>
                      <span style={{ color: e.failed > 0 ? 'var(--danger)' : 'inherit' }}>{e.failed}</span>
                    </td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {(() => {
                        const b = matchedStats.get(e);
                        if (!b) return <span className="muted">—</span>;
                        if (!b.trackingEnabled) return <span className="muted" title="Tracking was off for this broadcast">off</span>;
                        return (
                          <>
                            <strong>{b.uniqueOpens}</strong>
                            <span className="muted"> / </span>
                            <strong>{b.uniqueClicks}</strong>
                          </>
                        );
                      })()}
                    </td>
                    <td className="muted" style={{ textAlign: 'center' }}>
                      {expanded === i ? '▾' : '▸'}
                    </td>
                  </tr>
                  {expanded === i && (
                    <tr>
                      <td colSpan={7} style={{ background: 'var(--bg)' }}>
                        <div style={{ padding: 12, fontSize: 13 }}>
                          <div className="row" style={{ marginBottom: 12, gap: 8 }}>
                            <button
                              className="primary"
                              style={{ padding: '6px 14px', fontSize: 13 }}
                              onClick={(ev) => { ev.stopPropagation(); openPreview(e); }}
                            >
                              Preview email
                            </button>
                          </div>
                          <DetailRow label="Recipients" value={String(e.recipients)} />
                          {e.sources && <DetailRow label="Tags filter" value={e.sources} />}
                          {e.excludedCount ? <DetailRow label="Excluded recipients" value={String(e.excludedCount)} /> : null}
                          {e.testEmail && <DetailRow label="Test email" value={e.testEmail} />}
                          {e.recipientEmails && (
                            <RecipientsBlock emails={e.recipientEmails} />
                          )}
                          {e.errors && <DetailRow label="Errors" value={e.errors} mono />}
                          {matchedStats.get(e) && (
                            <BroadcastEngagement
                              broadcast={matchedStats.get(e)!}
                              subsByEmail={subsByEmail}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {preview && (
        <PreviewModal
          html={preview.html}
          title={`Preview: ${preview.subject}`}
          onClose={() => setPreview(null)}
        />
      )}
    </>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 700 }}>{value}</div>
    </div>
  );
}

function DetailRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 4 }}>
      <span className="muted" style={{ minWidth: 120 }}>{label}:</span>
      <span style={mono ? { fontFamily: 'ui-monospace, monospace', fontSize: 12, wordBreak: 'break-all' } : undefined}>
        {value}
      </span>
    </div>
  );
}

function EmailLink({ email }: { email: string }) {
  return <Link to={`/subscriber/${encodeURIComponent(email)}`}>{email}</Link>;
}

function BroadcastEngagement({
  broadcast,
  subsByEmail,
}: {
  broadcast: BroadcastStat;
  subsByEmail: Map<string, Subscriber>;
}) {
  const [detail, setDetail] = useState<BroadcastDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setDetailError(null);
    if (!broadcast.trackingEnabled) return;
    api.getBroadcastDetail(broadcast.id)
      .then((r) => { if (!cancelled) setDetail(r); })
      .catch((e: Error) => { if (!cancelled) setDetailError(e.message); });
    return () => { cancelled = true; };
  }, [broadcast.id, broadcast.trackingEnabled]);

  if (!broadcast.trackingEnabled) {
    return (
      <p className="muted" style={{ margin: '12px 0 0', fontSize: 12 }}>
        Tracking was off for this broadcast — no open/click data was collected.
      </p>
    );
  }
  if (detailError) return <div className="error" style={{ marginTop: 12 }}>Engagement load failed: {detailError}</div>;
  if (!detail) return <p className="muted" style={{ margin: '12px 0 0', fontSize: 12 }}>Loading engagement…</p>;

  const hasPurchased = (email: string) =>
    (subsByEmail.get(email.trim().toLowerCase())?.tags ?? []).includes('purchased');
  const allClickers = Array.from(new Set(detail.links.flatMap((l) => l.clickers.map((c) => c.email))));
  const warmLeads = allClickers.filter((email) => !hasPurchased(email));

  return (
    <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>
        Engagement: {detail.openers.length} opened · {allClickers.length} clicked
      </div>

      {detail.openers.length > 0 && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 8, alignItems: 'flex-start' }}>
          <span className="muted" style={{ minWidth: 120 }}>Opened:</span>
          <span style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
            {detail.openers.map((o) => <EmailLink key={o.email} email={o.email} />)}
          </span>
        </div>
      )}

      {detail.links.length > 0 && (
        <table style={{ marginTop: 8 }}>
          <thead>
            <tr>
              <th>Link</th>
              <th style={{ textAlign: 'right' }}>Clicks</th>
              <th>Who clicked</th>
            </tr>
          </thead>
          <tbody>
            {detail.links.map((l) => (
              <tr key={l.linkId}>
                <td style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <span title={l.url}>{l.label || l.url || `link #${l.linkId}`}</span>
                </td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{l.clickers.length}</td>
                <td>
                  {l.clickers.length === 0 ? (
                    <span className="muted">—</span>
                  ) : (
                    <span style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
                      {l.clickers.map((c) => (
                        <span key={c.email} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <EmailLink email={c.email} />
                          {hasPurchased(c.email) && <span className="badge subscribed" style={{ fontSize: 10 }}>purchased</span>}
                        </span>
                      ))}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {warmLeads.length > 0 && (
        <div style={{ marginTop: 12, padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            Clicked but not tagged “purchased” ({warmLeads.length}) — your follow-up list:
          </div>
          <span style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
            {warmLeads.map((email) => <EmailLink key={email} email={email} />)}
          </span>
        </div>
      )}
    </div>
  );
}

function RecipientsBlock({ emails }: { emails: string }) {
  const [open, setOpen] = useState(false);
  const list = emails.split(',').map((s) => s.trim()).filter(Boolean);
  if (list.length === 0) return null;
  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 4, alignItems: 'flex-start' }}>
      <span className="muted" style={{ minWidth: 120 }}>Recipient emails:</span>
      <div style={{ flex: 1 }}>
        <button
          type="button"
          className="link-btn"
          onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
          style={{ padding: 0 }}
        >
          {open ? `Hide ${list.length}` : `Show ${list.length} email${list.length === 1 ? '' : 's'}`}
        </button>
        {open && (
          <ul style={{
            margin: '6px 0 0',
            padding: '8px 12px',
            listStyle: 'none',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            maxHeight: 200,
            overflowY: 'auto',
            fontFamily: 'ui-monospace, monospace',
            fontSize: 12,
          }}>
            {list.map((email) => (
              <li key={email} style={{ padding: '2px 0' }}>
                <Link to={`/subscriber/${encodeURIComponent(email)}`}>{email}</Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
